import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Target, MessageSquare, Activity, PieChart, ArrowLeft } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';

function MatchDetails() {
  const { filename } = useParams();
  const [matchInfo, setMatchInfo] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [commentary, setCommentary] = useState({ 1: null, 2: null });
  const [liveDetails, setLiveDetails] = useState(null);
  const [wagonWheel, setWagonWheel] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (filename) {
      fetchMatchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename]);

  const fetchMatchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [infoRes, scorecardRes, liveRes, wagonRes] = await Promise.all([
        apiService.getMatchByFilename(filename).catch(() => null),
        apiService.getScorecard(filename).catch(() => null),
        apiService.getLiveDetails(filename).catch(() => null),
        apiService.getWagonWheel(filename).catch(() => null)
      ]);

      setMatchInfo(infoRes?.data || null);
      setScorecard(scorecardRes?.data || null);
      setLiveDetails(liveRes?.data || null);
      setWagonWheel(wagonRes?.data || null);

      // Try to fetch commentary for both innings
      try {
        const [comm1, comm2] = await Promise.all([
          apiService.getMatchCommentary(filename, 1).catch(() => null),
          apiService.getMatchCommentary(filename, 2).catch(() => null)
        ]);
        setCommentary({ 1: comm1?.data || null, 2: comm2?.data || null });
      } catch (err) {
        // Commentary might not be available
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading match details..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchMatchData} />;
  if (!matchInfo) return <ErrorMessage message="Match not found" />;

  const tabs = [
    { id: 'info', label: 'Match Info', icon: Calendar },
    { id: 'scorecard', label: 'Scorecard', icon: Target },
    { id: 'commentary', label: 'Commentary', icon: MessageSquare },
    { id: 'live', label: 'Live Details', icon: Activity },
    { id: 'wagon', label: 'Wagon Wheel', icon: PieChart }
  ];

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '32px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Header */}
      <div>
        <Link
          to="/matches"
          style={{
            color: '#007AFF',
            textDecoration: 'none',
            marginBottom: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '17px',
            fontWeight: '500',
            transition: 'opacity 0.2s'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        >
          <ArrowLeft size={20} />
          Back to Matches
        </Link>
        <h1 style={{
          fontSize: '34px',
          fontWeight: '700',
          color: '#1d1d1f',
          marginTop: '12px',
          letterSpacing: '-0.5px',
          margin: 0
        }}>
          {matchInfo.title || matchInfo.short_title}
        </h1>
        <p style={{
          color: '#86868b',
          marginTop: '8px',
          fontSize: '17px',
          fontWeight: '500'
        }}>
          {matchInfo.status_note || matchInfo.result || 'Match Details'}
        </p>
      </div>

      {/* Match Summary Card */}
      <Card>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '24px'
        }}>
          {/* Team A */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '12px',
            border: '1px solid #e5e5e7'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '12px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Team A
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {matchInfo.teama?.logo_url && (
                <img
                  src={matchInfo.teama.logo_url}
                  alt={matchInfo.teama.name}
                  style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '17px' }}>
                  {matchInfo.teama?.name}
                </p>
                <p style={{ fontSize: '20px', color: '#007AFF', margin: '4px 0 0 0', fontWeight: '700' }}>
                  {matchInfo.teama?.scores_full || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Team B */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '12px',
            border: '1px solid #e5e5e7'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '12px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Team B
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {matchInfo.teamb?.logo_url && (
                <img
                  src={matchInfo.teamb.logo_url}
                  alt={matchInfo.teamb.name}
                  style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '17px' }}>
                  {matchInfo.teamb?.name}
                </p>
                <p style={{ fontSize: '20px', color: '#007AFF', margin: '4px 0 0 0', fontWeight: '700' }}>
                  {matchInfo.teamb?.scores_full || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Venue */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '12px',
            border: '1px solid #e5e5e7'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Venue
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {matchInfo.venue?.name || 'N/A'}
            </p>
            <p style={{ fontSize: '13px', color: '#86868b', margin: '4px 0 0 0' }}>
              {matchInfo.venue?.location || ''}
            </p>
          </div>

          {/* Date */}
          <div style={{
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '12px',
            border: '1px solid #e5e5e7'
          }}>
            <p style={{
              fontSize: '13px',
              color: '#86868b',
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Date
            </p>
            <p style={{ fontWeight: '600', color: '#1d1d1f', margin: 0, fontSize: '15px' }}>
              {matchInfo.date_start ? new Date(matchInfo.date_start).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : 'N/A'}
            </p>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '4px',
        borderBottom: '1px solid #e5e5e7',
        overflowX: 'auto'
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '12px 20px',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #007AFF' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === tab.id ? '#007AFF' : '#86868b',
                fontWeight: activeTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '15px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = '#1d1d1f';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tab.id) {
                  e.currentTarget.style.color = '#86868b';
                }
              }}
            >
              <Icon style={{ width: '18px', height: '18px' }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content - Match Info */}
      {activeTab === 'info' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          <Card>
            <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '12px', color: '#1d1d1f' }}>
              Toss
            </h3>
            <p style={{ color: '#1d1d1f', fontSize: '15px', lineHeight: '1.5' }}>
              {matchInfo.toss?.text || 'N/A'}
            </p>
          </Card>
          <Card>
            <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '12px', color: '#1d1d1f' }}>
              Result
            </h3>
            <p style={{ color: '#1d1d1f', fontSize: '15px', lineHeight: '1.5' }}>
              {matchInfo.result || matchInfo.status_note || 'N/A'}
            </p>
          </Card>
          <Card>
            <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '12px', color: '#1d1d1f' }}>
              Umpires
            </h3>
            <p style={{ color: '#1d1d1f', fontSize: '15px', lineHeight: '1.5' }}>
              {matchInfo.umpires || 'N/A'}
            </p>
          </Card>
          <Card>
            <h3 style={{ fontSize: '17px', fontWeight: '600', marginBottom: '12px', color: '#1d1d1f' }}>
              Player of the Match
            </h3>
            <p style={{ color: '#1d1d1f', fontSize: '15px', lineHeight: '1.5' }}>
              {matchInfo.man_of_the_match?.name || scorecard?.man_of_the_match?.name || 'N/A'}
            </p>
          </Card>
        </div>
      )}

      {/* Tab Content - Scorecard */}
      {activeTab === 'scorecard' && scorecard && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {scorecard.innings?.map((inning, idx) => (
            <Card key={idx}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1d1d1f' }}>
                {inning.name || `Innings ${inning.number}`}
              </h3>

              {/* Batsmen */}
              <div style={{ marginBottom: '32px' }}>
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: '#86868b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Batting
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f7' }}>
                        <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Batsman</th>
                        <th style={tableHeaderStyle}>Runs</th>
                        <th style={tableHeaderStyle}>Balls</th>
                        <th style={tableHeaderStyle}>4s</th>
                        <th style={tableHeaderStyle}>6s</th>
                        <th style={tableHeaderStyle}>SR</th>
                        <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Dismissal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inning.batsmen?.map((batsman, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e5e5e7' }}>
                          <td style={{ ...tableCellStyle, textAlign: 'left', fontWeight: '500' }}>
                            {batsman.name}
                          </td>
                          <td style={{ ...tableCellStyle, fontWeight: '600', color: '#007AFF' }}>
                            {batsman.runs}
                          </td>
                          <td style={tableCellStyle}>{batsman.balls_faced}</td>
                          <td style={tableCellStyle}>{batsman.fours}</td>
                          <td style={tableCellStyle}>{batsman.sixes}</td>
                          <td style={tableCellStyle}>{batsman.strike_rate}</td>
                          <td style={{ ...tableCellStyle, textAlign: 'left', fontSize: '13px', color: '#86868b' }}>
                            {batsman.how_out || 'Not out'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bowlers */}
              <div>
                <h4 style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  marginBottom: '16px',
                  color: '#86868b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  Bowling
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '14px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f7' }}>
                        <th style={{ ...tableHeaderStyle, textAlign: 'left' }}>Bowler</th>
                        <th style={tableHeaderStyle}>Overs</th>
                        <th style={tableHeaderStyle}>Runs</th>
                        <th style={tableHeaderStyle}>Wickets</th>
                        <th style={tableHeaderStyle}>Econ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inning.bowlers?.map((bowler, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e5e5e7' }}>
                          <td style={{ ...tableCellStyle, textAlign: 'left', fontWeight: '500' }}>
                            {bowler.name}
                          </td>
                          <td style={tableCellStyle}>{bowler.overs}</td>
                          <td style={tableCellStyle}>{bowler.runs_conceded}</td>
                          <td style={{ ...tableCellStyle, fontWeight: '600', color: '#34C759' }}>
                            {bowler.wickets}
                          </td>
                          <td style={tableCellStyle}>{bowler.econ}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Tab Content - Commentary */}
      {activeTab === 'commentary' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {[1, 2].map((inn) => {
            const commData = commentary[inn];
            const commentaries = commData?.commentaries || commData?.data?.commentaries || commData?.response?.commentaries || [];

            return (
              <Card key={inn}>
                <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1d1d1f' }}>
                  Innings {inn} Commentary
                </h3>
                {commentaries.length > 0 ? (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {commentaries.slice(0, 50).map((comm, idx) => {
                        const over = comm.over || comm.over_number || 'N/A';
                        const ball = comm.ball || comm.ball_number || 'N/A';
                        const runs = comm.run || comm.runs || comm.bat_run || 0;
                        const isWicket = comm.wicket || comm.is_wicket || false;
                        const isFour = comm.four || comm.is_four || false;
                        const isSix = comm.six || comm.is_six || false;
                        const text = comm.commentary || comm.text || comm.event || '';

                        return (
                          <div
                            key={idx}
                            style={{
                              padding: '14px',
                              backgroundColor: isWicket ? '#FFE5E5' : isSix ? '#E3F2FD' : isFour ? '#FFF3E0' : '#f9f9f9',
                              borderRadius: '10px',
                              borderLeft: `4px solid ${isWicket ? '#FF3B30' : isSix ? '#007AFF' : isFour ? '#FF9500' : '#86868b'}`,
                              display: 'flex',
                              gap: '16px',
                              alignItems: 'flex-start'
                            }}
                          >
                            <div style={{ minWidth: '70px', textAlign: 'center' }}>
                              <div style={{ fontWeight: '700', color: '#1d1d1f', fontSize: '15px' }}>
                                {over}.{ball}
                              </div>
                              <div style={{ fontSize: '13px', marginTop: '6px', display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {runs > 0 && (
                                  <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#34C759',
                                    color: 'white',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '11px'
                                  }}>
                                    {runs}
                                  </span>
                                )}
                                {isWicket && (
                                  <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#FF3B30',
                                    color: 'white',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '11px'
                                  }}>
                                    W
                                  </span>
                                )}
                                {isFour && (
                                  <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#FF9500',
                                    color: 'white',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '11px'
                                  }}>
                                    4
                                  </span>
                                )}
                                {isSix && (
                                  <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#007AFF',
                                    color: 'white',
                                    borderRadius: '4px',
                                    fontWeight: '600',
                                    fontSize: '11px'
                                  }}>
                                    6
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              {text && (
                                <p style={{ margin: 0, color: '#1d1d1f', fontSize: '15px', lineHeight: '1.5' }}>
                                  {text}
                                </p>
                              )}
                              {(comm.batsman || comm.bowler) && (
                                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#86868b' }}>
                                  {comm.batsman && `Batsman: ${comm.batsman}`}
                                  {comm.batsman && comm.bowler && ' • '}
                                  {comm.bowler && `Bowler: ${comm.bowler}`}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <p style={{
                    color: '#86868b',
                    textAlign: 'center',
                    padding: '48px',
                    fontSize: '15px'
                  }}>
                    Commentary not available for this innings
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Tab Content - Live Details */}
      {activeTab === 'live' && (
        <Card>
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1d1d1f' }}>
            Live Match Details
          </h3>
          {liveDetails && liveDetails.commentaries && liveDetails.commentaries.length > 0 ? (
            <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {liveDetails.commentaries.slice(0, 50).map((comm, idx) => {
                  const over = comm.over || 'N/A';
                  const ball = comm.ball || 'N/A';
                  const runs = comm.run || comm.bat_run || 0;
                  const isWicket = comm.wicket || false;
                  const isFour = comm.four || false;
                  const isSix = comm.six || false;

                  return (
                    <div
                      key={idx}
                      style={{
                        padding: '12px',
                        backgroundColor: '#f9f9f9',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderLeft: `3px solid ${isWicket ? '#FF3B30' : isSix ? '#007AFF' : isFour ? '#FF9500' : '#86868b'}`
                      }}
                    >
                      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: 1 }}>
                        <span style={{ fontWeight: '700', color: '#1d1d1f', minWidth: '60px', fontSize: '14px' }}>
                          {over}.{ball}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          {runs > 0 && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#34C759',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              {runs} run{runs > 1 ? 's' : ''}
                            </span>
                          )}
                          {isWicket && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#FF3B30',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              Wicket
                            </span>
                          )}
                          {isFour && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#FF9500',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              Four
                            </span>
                          )}
                          {isSix && (
                            <span style={{
                              padding: '4px 10px',
                              backgroundColor: '#007AFF',
                              color: 'white',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: '600'
                            }}>
                              Six
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p style={{ color: '#86868b', textAlign: 'center', padding: '48px', fontSize: '15px' }}>
              Live details not available for this match
            </p>
          )}
        </Card>
      )}

      {/* Tab Content - Wagon Wheel */}
      {activeTab === 'wagon' && (
        <Card>
          <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1d1d1f' }}>
            Wagon Wheel - Shot Placement
          </h3>
          {wagonWheel ? (
            <p style={{ color: '#86868b', textAlign: 'center', padding: '48px', fontSize: '15px' }}>
              Shot placement visualization data available. Advanced visualization coming soon.
            </p>
          ) : (
            <p style={{ color: '#86868b', textAlign: 'center', padding: '48px', fontSize: '15px' }}>
              Wagon wheel data not available for this match
            </p>
          )}
        </Card>
      )}
    </div>
  );
}

// Reusable table styles
const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'center',
  fontWeight: '600',
  fontSize: '13px',
  color: '#1d1d1f',
  textTransform: 'uppercase',
  letterSpacing: '0.5px'
};

const tableCellStyle = {
  padding: '12px 16px',
  textAlign: 'center',
  fontSize: '14px',
  color: '#1d1d1f'
};

export default MatchDetails;
